/**
 * Helper функции для работы с ролевой системой
 * Обеспечивают обратную совместимость при переходе на новую ролевую систему
 */

import { UserRole, UserRolesResponse } from "../services/api/auth.api";
import { EventRole } from "../types/enums/event-roles.enum";
import { InternalRole } from "../types/enums/internal-roles.enum";
import { SystemRole, UserContextRole } from "../types/enums/user-roles";

/**
 * Преобразует UserRole в UserContextRole для обратной совместимости.
 *
 * ADR-012: значение ВАЛИДИРУЕТСЯ против enum, а не слепо кастуется — иначе
 * нераспознанная роль (например, 'ORGANIZER' вместо 'organizer' до нормализации
 * контракта) молча деградировала role-aware меню до уровня обычного USER,
 * без единого сигнала в консоли. Теперь: null + console.warn.
 */
export const convertUserRoleToContextRole = (
  userRole: UserRole | null,
): UserContextRole | null => {
  if (!userRole) return null;

  if (userRole.type === "internal") {
    if (Object.values(InternalRole).includes(userRole.role as InternalRole)) {
      return userRole.role as InternalRole;
    }
    console.warn(
      "⚠️ role-helpers: internal-роль не входит в enum InternalRole (ADR-012)",
      { role: userRole.role },
    );
    return null;
  } else if (userRole.type === "event") {
    if (Object.values(EventRole).includes(userRole.role as EventRole)) {
      return userRole.role as EventRole;
    }
    console.warn(
      "⚠️ role-helpers: event-роль не входит в enum EventRole (ADR-012) — " +
        "проверьте регистр имени в API-контракте",
      { role: userRole.role },
    );
    return null;
  }

  return null;
};

/**
 * Проверяет, соответствует ли UserRole определенной роли
 */
export const isUserRole = (
  userRole: UserRole | null,
  targetRole: EventRole | InternalRole | SystemRole,
): boolean => {
  if (!userRole) return false;
  return userRole.role === targetRole;
};

/**
 * Проверяет, является ли UserRole организатором
 * CRITICAL: Проверяет И тип роли ('event') И значение роли
 */
export const isOrganizerRole = (userRole: UserRole | null): boolean => {
  if (!userRole) return false;
  // ИСПРАВЛЕНО: добавлена проверка type === 'event'
  return (
    userRole.type === "event" &&
    (userRole.role === EventRole.ORGANIZER ||
      userRole.role === EventRole.EVENT_ADMIN)
  );
};

/**
 * Проверяет, является ли UserRole поставщиком
 * CRITICAL: Проверяет И тип роли ('event') И значение роли
 */
export const isSupplierRole = (userRole: UserRole | null): boolean => {
  if (!userRole) return false;
  // ИСПРАВЛЕНО: добавлена проверка type === 'event'
  return userRole.type === "event" && userRole.role === EventRole.SUPPLIER;
};

/**
 * Проверяет, является ли UserRole участником
 * CRITICAL: Проверяет И тип роли ('event') И значение роли
 */
export const isParticipantRole = (userRole: UserRole | null): boolean => {
  if (!userRole) return false;
  // ИСПРАВЛЕНО: добавлена проверка type === 'event'
  return userRole.type === "event" && userRole.role === EventRole.PARTICIPANT;
};

/**
 * Проверяет, является ли UserRole спикером
 * CRITICAL: Проверяет И тип роли ('event') И значение роли
 */
export const isSpeakerRole = (userRole: UserRole | null): boolean => {
  if (!userRole) return false;
  // ИСПРАВЛЕНО: добавлена проверка type === 'event'
  return userRole.type === "event" && userRole.role === EventRole.SPEAKER;
};

/**
 * Проверяет, является ли UserRole администратором платформы
 * CRITICAL: Проверяет И тип роли ('internal') И значение роли
 */
export const isAdminRole = (userRole: UserRole | null): boolean => {
  if (!userRole) return false;
  // ИСПРАВЛЕНО: добавлена проверка type === 'internal'
  return (
    userRole.type === "internal" &&
    (userRole.role === InternalRole.SUPER_ADMIN ||
      userRole.role === InternalRole.PLATFORM_ADMIN)
  );
};

const ADMIN_OR_MANAGER_ROLES: readonly string[] = [
  InternalRole.SUPER_ADMIN,
  InternalRole.PLATFORM_ADMIN,
  InternalRole.PLATFORM_MANAGER,
];

/**
 * Есть ли у пользователя роль администратора или менеджера платформы. Три роли
 * перечислены по серверной `isAdminOrManager` (BE `shared/utils/role-helpers.ts`).
 *
 * Смотрит на ВСЕ внутренние роли пользователя, а не на выбранную в переключателе.
 * Роли не получены или роль снята — ответ «нет». Это подсказка экрану, что
 * предлагать человеку, а не проверка доступа: решение о доступе — за сервером.
 */
export const hasAdminOrManagerRole = (
  userRoles: UserRolesResponse | null,
): boolean => {
  if (!userRoles) return false;
  return userRoles.internalRoles.some(
    (item) =>
      item.type === "internal" &&
      item.isActive &&
      ADMIN_OR_MANAGER_ROLES.includes(item.role),
  );
};

/**
 * Получает отображаемое имя роли
 */
export const getRoleDisplayName = (userRole: UserRole | null): string => {
  if (!userRole) {
    console.warn("⚠️ getRoleDisplayName: попытка получить имя для null роли");
    return "Роль не выбрана";
  }

  const roleNames: Record<string, string> = {
    // Event roles
    [EventRole.ORGANIZER]: "Организатор",
    [EventRole.PARTICIPANT]: "Участник",
    [EventRole.SPEAKER]: "Спикер",
    [EventRole.SUPPLIER]: "Поставщик",
    [EventRole.EVENT_ADMIN]: "Администратор события",
    [EventRole.CO_ORGANIZER]: "Совместный организатор",
    [EventRole.EXECUTOR]: "Исполнитель",

    // Internal roles
    [InternalRole.SUPER_ADMIN]: "Супер администратор",
    [InternalRole.PLATFORM_ADMIN]: "Администратор платформы",
    [InternalRole.PLATFORM_MANAGER]: "Менеджер платформы",
    [InternalRole.CONTENT_MODERATOR]: "Модератор контента",
    [InternalRole.SUPPORT_AGENT]: "Агент поддержки",

    // System roles (legacy)
    [SystemRole.ADMIN]: "Администратор",
    [SystemRole.MANAGER]: "Менеджер",
    [SystemRole.USER]: "Пользователь",
    [SystemRole.GUEST]: "Гость",
  };

  return roleNames[userRole.role] || userRole.role;
};

/**
 * Проверяет, имеет ли пользователь разрешение на управление событиями
 */
export const canManageEvents = (userRole: UserRole | null): boolean => {
  if (!userRole) return false;
  return (
    userRole.permissions.includes("manage_events") ||
    userRole.permissions.includes("create_events") ||
    isOrganizerRole(userRole) ||
    isAdminRole(userRole)
  );
};

/**
 * Проверяет, имеет ли пользователь разрешение на просмотр аналитики
 */
export const canViewAnalytics = (userRole: UserRole | null): boolean => {
  if (!userRole) return false;
  return (
    userRole.permissions.includes("view_analytics") ||
    userRole.permissions.includes("view_dashboard") ||
    isAdminRole(userRole) ||
    isOrganizerRole(userRole)
  );
};

/**
 * Проверяет, имеет ли пользователь разрешение на управление пользователями
 */
export const canManageUsers = (userRole: UserRole | null): boolean => {
  if (!userRole) return false;
  return (
    userRole.permissions.includes("manage_users") ||
    userRole.permissions.includes("user_management") ||
    isAdminRole(userRole)
  );
};

/**
 * Проверяет, имеет ли пользователь разрешение на управление финансами
 */
export const canManageFinance = (userRole: UserRole | null): boolean => {
  if (!userRole) return false;
  return (
    userRole.permissions.includes("manage_finance") ||
    userRole.permissions.includes("view_financials") ||
    isAdminRole(userRole) ||
    isOrganizerRole(userRole)
  );
};

/**
 * Проверяет, имеет ли пользователь разрешение на модерацию контента
 */
export const canModerateContent = (userRole: UserRole | null): boolean => {
  if (!userRole) return false;
  return (
    userRole.permissions.includes("moderate_content") ||
    userRole.permissions.includes("content_moderation") ||
    (userRole.type === "internal" &&
      userRole.role === InternalRole.CONTENT_MODERATOR) ||
    isAdminRole(userRole)
  );
};

/**
 * Проверяет, имеет ли пользователь разрешение на поддержку пользователей
 */
export const canSupportUsers = (userRole: UserRole | null): boolean => {
  if (!userRole) return false;
  return (
    userRole.permissions.includes("support_users") ||
    userRole.permissions.includes("customer_support") ||
    (userRole.type === "internal" &&
      userRole.role === InternalRole.SUPPORT_AGENT) ||
    isAdminRole(userRole)
  );
};

/**
 * Проверяет, имеет ли пользователь разрешение на управление системой
 */
export const canManageSystem = (userRole: UserRole | null): boolean => {
  if (!userRole) return false;
  return (
    userRole.permissions.includes("system_management") ||
    userRole.permissions.includes("admin_panel") ||
    (userRole.type === "internal" && userRole.role === InternalRole.SUPER_ADMIN)
  );
};
